import type {
  CollectionPreviewResponse,
  ConfirmResponse,
  UploadMetaResponse,
  VendorDetectionResult,
} from "../api/client";

// ─── 타입 ─────────────────────────────────────────────────────

export type UploadStatus =
  | "idle"
  | "uploading"
  | "matched"             // 업체 감지 완료, 분석 대기 중
  | "assigning"           // 미매칭 → 사용자가 업체 지정한 직후 (1초 이동 애니메이션)
  | "dateConfirm"
  | "queued"
  | "parsing"
  | "needsVendor"
  | "preview"
  | "confirming"
  | "confirmed"
  | "alreadyAnalyzed"     // 동일 파일(해시 중복): 이미 분석·확정된 상태 — 우측 패널에 완료로 표시
  | "file_expired"        // Step 2 파싱 시 메모리 버퍼 만료 — 재업로드 필요
  | "duplicateConflict"   // Step 2에서 409: 이미 해당 날짜 데이터 존재 — 사용자 확인 필요
  | "error";

/** localStorage에 저장되는 직렬화 가능한 엔트리 */
export interface StoredFileEntry {
  fileName: string;
  fileSize: number;
  status: UploadStatus;
  preview: CollectionPreviewResponse | null;
  confirmResult: ConfirmResponse | null;
  uploadMeta: UploadMetaResponse | null;
  vendorNeeded: VendorDetectionResult | null;
  selectedVendor: string;
  error: string | null;
  priceDate: string;
  targetVendor: string | null;
  duplicateInfo: { existingCollectionId: string; existingStatus: string } | null;
}

interface UploadSession {
  entries: StoredFileEntry[];
  savedAt: number; // epoch ms
}

// ─── 상수 ─────────────────────────────────────────────────────

const STORAGE_KEY = "phoneboard:upload_session:v3";
const MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24시간

// ─── 저장 가능한 상태만 필터 ───────────────────────────────────

/** 진행 중(uploading/confirming/parsing)인 항목은 복원 불가하므로 저장에서 제외 */
const PERSISTABLE_STATUSES: Set<UploadStatus> = new Set([
  "dateConfirm",
  "matched",
  "needsVendor",
  "preview",
  "file_expired",
  "duplicateConflict",
  "error",
  // confirmed / alreadyAnalyzed 는 저장하지 않음 — 재진입 시 자동 정리
]);

// ─── API ──────────────────────────────────────────────────────

export function readUploadSession(): StoredFileEntry[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];

    const session: UploadSession = JSON.parse(raw);

    // 만료 체크
    if (Date.now() - session.savedAt > MAX_AGE_MS) {
      clearUploadSession();
      return [];
    }

    // 저장 정책이 바뀐 상태(confirmed 등)는 복원에서도 제외
    return session.entries.filter((e) => PERSISTABLE_STATUSES.has(e.status));
  } catch {
    clearUploadSession();
    return [];
  }
}

export function writeUploadSession(entries: StoredFileEntry[]): void {
  if (typeof window === "undefined") return;

  const persistable = entries.filter((e) => PERSISTABLE_STATUSES.has(e.status));

  if (persistable.length === 0) {
    clearUploadSession();
    return;
  }

  const session: UploadSession = {
    entries: persistable,
    savedAt: Date.now(),
  };
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
}

export function clearUploadSession(): void {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(STORAGE_KEY);
}

/** FileEntry → 직렬화 가능한 StoredFileEntry */
export function toStoredEntry(entry: {
  file: File | null;
  fileName: string;
  fileSize: number;
  status: UploadStatus;
  preview: CollectionPreviewResponse | null;
  confirmResult: ConfirmResponse | null;
  uploadMeta: UploadMetaResponse | null;
  vendorNeeded: VendorDetectionResult | null;
  selectedVendor: string;
  error: string | null;
  priceDate: string;
  targetVendor: string | null;
  duplicateInfo: { existingCollectionId: string; existingStatus: string } | null;
}): StoredFileEntry {
  return {
    fileName: entry.fileName,
    fileSize: entry.fileSize,
    status: entry.status,
    preview: entry.preview,
    confirmResult: entry.confirmResult,
    uploadMeta: entry.uploadMeta,
    vendorNeeded: entry.vendorNeeded,
    selectedVendor: entry.selectedVendor,
    error: entry.error,
    priceDate: entry.priceDate,
    targetVendor: entry.targetVendor,
    duplicateInfo: entry.duplicateInfo,
  };
}
